import React from "react";
import { IconAngry, IconHeartRed, IconLike, IconSmile, IconStunned, IconWantToCry } from "../icons/emoticons";
import vi from '../img/language/vi.png';
import en from '../img/language/en.png';
import lo from '../img/language/lo.png';
import bgr0 from '../img/background/background-0.png';
import bgr1 from '../img/background/background-1.png';
import bgr2 from '../img/background/background-2.png';
import bgr3 from '../img/background/background-3.png';
import bgr4 from '../img/background/background-4.png';
import bgr5 from '../img/background/background-5.png';
import bgr6 from '../img/background/background-6.png';
import bgr7 from '../img/background/background-7.png';
import bgr8 from '../img/background/background-8.png';
import bgr9 from '../img/background/background-9.png';
import bgr10 from '../img/background/background-10.png';
import bgr11 from '../img/background/background-11.png';

/**
 * Common namespace constants from the XMPP RFCs and XEPs.
 *
 * @typedef { Object } NS
 * @property {string} NS.HTTPBIND - HTTP BIND namespace from XEP 124.
 * @property {string} NS.BOSH - BOSH namespace from XEP 206.
 * @property {string} NS.CLIENT - Main XMPP client namespace.
 * @property {string} NS.AUTH - Legacy authentication namespace.
 * @property {string} NS.ROSTER - Roster operations namespace.
 * @property {string} NS.PROFILE - Profile namespace.
 * @property {string} NS.DISCO_INFO - Service discovery info namespace from XEP 30.
 * @property {string} NS.DISCO_ITEMS - Service discovery items namespace from XEP 30.
 * @property {string} NS.MUC - Multi-User Chat namespace from XEP 45.
 * @property {string} NS.SASL - XMPP SASL namespace from RFC 3920.
 * @property {string} NS.STREAM - XMPP Streams namespace from RFC 3920.
 * @property {string} NS.BIND - XMPP Binding namespace from RFC 3920 and RFC 6120.
 * @property {string} NS.SESSION - XMPP Session namespace from RFC 3920.
 * @property {string} NS.XHTML_IM - XHTML-IM namespace from XEP 71.
 * @property {string} NS.XHTML - XHTML body namespace from XEP 71.
 * @property {string} NS.STANZAS
 * @property {string} NS.FRAMING
 */
export const NS = {
  HTTPBIND: 'http://jabber.org/protocol/httpbind',
  BOSH: 'urn:xmpp:xbosh',
  CLIENT: 'jabber:client',
  AUTH: 'jabber:iq:auth',
  ROSTER: 'jabber:iq:roster',
  PROFILE: 'jabber:iq:profile',
  DISCO_INFO: 'http://jabber.org/protocol/disco#info',
  DISCO_ITEMS: 'http://jabber.org/protocol/disco#items',
  MUC: 'http://jabber.org/protocol/muc',
  SASL: 'urn:ietf:params:xml:ns:xmpp-sasl',
  STREAM: 'http://etherx.jabber.org/streams',
  FRAMING: 'urn:ietf:params:xml:ns:xmpp-framing',
  BIND: 'urn:ietf:params:xml:ns:xmpp-bind',
  SESSION: 'urn:ietf:params:xml:ns:xmpp-session',
  VERSION: 'jabber:iq:version',
  STANZAS: 'urn:ietf:params:xml:ns:xmpp-stanzas',
  XHTML_IM: 'http://jabber.org/protocol/xhtml-im',
  XHTML: 'http://www.w3.org/1999/xhtml',
  VT_MESSAGE_EVENT: 'vt:message:event',
  CONFIG: 'config',
  PING: 'urn:xmpp:ping',
  CREATE: 'create', // Tạo nhóm
  RENAME: 'rename', // Đổi tên nhóm
  INVITE: 'invite', // Thêm thành viên nhóm
  LEAVE: 'leave', // Rời nhóm
  MAKE_ADMIN: 'makeAdmin', //Trao quản trị viên
  KICK: 'kick', //Kick thành viên ra khỏi nhóm
  DISSOLVE: 'dissolve', // Giải tán nhóm
  KICK: 'kick',
  GROUP_PRIVATE: 'groupPrivate',
};

/**
 * Contains allowed tags, tag attributes, and css properties.
 * Used in the {@link Strophe.createHtml} function to filter incoming html into the allowed XHTML-IM subset.
 * See [XEP-0071](http://xmpp.org/extensions/xep-0071.html#profile-summary) for the list of recommended
 * allowed tags and their attributes.
 */
export const XHTML = {
  tags: ['a', 'blockquote', 'br', 'cite', 'em', 'img', 'li', 'ol', 'p', 'span', 'strong', 'ul', 'body'],
  attributes: {
    'a': ['href'],
    'blockquote': ['style'],
    /** @type {never[]} */
    'br': [],
    'cite': ['style'],
    /** @type {never[]} */
    'em': [],
    'img': ['src', 'alt', 'style', 'height', 'width'],
    'li': ['style'],
    'ol': ['style'],
    'p': ['style'],
    'span': ['style'],
    /** @type {never[]} */
    'strong': [],
    'ul': ['style'],
    /** @type {never[]} */
    'body': [],
  },
  css: [
    'background-color',
    'color',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'margin-left',
    'margin-right',
    'text-align',
    'text-decoration',
  ],
};

/** @typedef {number} connstatus */

/**
 * Connection status constants for use by the connection handler
 * callback.
 *
 * @typedef {Object} Status
 * @property {connstatus} Status.ERROR - An error has occurred
 * @property {connstatus} Status.CONNECTING - The connection is currently being made
 * @property {connstatus} Status.CONNFAIL - The connection attempt failed
 * @property {connstatus} Status.AUTHENTICATING - The connection is authenticating
 * @property {connstatus} Status.AUTHFAIL - The authentication attempt failed
 * @property {connstatus} Status.CONNECTED - The connection has succeeded
 * @property {connstatus} Status.DISCONNECTED - The connection has been terminated
 * @property {connstatus} Status.DISCONNECTING - The connection is currently being terminated
 * @property {connstatus} Status.ATTACHED - The connection has been attached
 * @property {connstatus} Status.REDIRECT - The connection has been redirected
 * @property {connstatus} Status.CONNTIMEOUT - The connection has timed out
 * @property {connstatus} Status.ATTACHFAIL - Failed to attach to a pre-existing session
 * @property {connstatus} Status.RECONNECTING - Not used by Strophe, but added for integrators
 */
export const Status = {
  ERROR: 0,
  CONNECTING: 1,
  CONNFAIL: 2,
  AUTHENTICATING: 3,
  AUTHFAIL: 4,
  CONNECTED: 5,
  DISCONNECTED: 6,
  DISCONNECTING: 7,
  ATTACHED: 8,
  REDIRECT: 9,
  CONNTIMEOUT: 10,
  BINDREQUIRED: 11,
  ATTACHFAIL: 12,
  RECONNECTING: 13,
};

export const ErrorCondition = {
  BAD_FORMAT: 'bad-format',
  CONFLICT: 'conflict',
  MISSING_JID_NODE: 'x-strophe-bad-non-anon-jid',
  NO_AUTH_MECH: 'no-auth-mech',
  UNKNOWN_REASON: 'unknown',
};

/**
 * @typedef {Object} LogLevel
 * @property {string} DEBUG
 * @property {string} INFO
 * @property {string} WARN
 * @property {string} ERROR
 * @property {string} FATAL
 */

/**
 * Logging level indicators.
 *
 * - Strophe.LogLevel.DEBUG - Debug output
 * - Strophe.LogLevel.INFO - Informational output
 * - Strophe.LogLevel.WARN - Warnings
 * - Strophe.LogLevel.ERROR - Errors
 * - Strophe.LogLevel.FATAL - Fatal errors
 */
export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
};

/**
 * DOM element types.
 *
 * - ElementType.NORMAL - Normal element.
 * - ElementType.TEXT - Text data element.
 * - ElementType.FRAGMENT - XHTML fragment element.
 */
export const ElementType = {
  NORMAL: 1,
  TEXT: 3,
  CDATA: 4,
  FRAGMENT: 11,
};

export const PING_INTERVAL_SECONDS = 5000

export const OPEN_STREAM_MSG_ID = 'open_stream'

export const MESSAGE_DELIVERED = {
  RECEIVED_FROM_MOBILE: null,
  SENT: -2, // Giá trị này không truyền trong bản tin delivered seen
  RECEIVED: -1,
  SEEN_1_MESSAGE: 0,
  SEEN_MULTI_MESSAGES: 1,
  FAILED: -3
}

export const PACKAGE_TYPE = {
  DELIVERED_SEEN: 'delivered_seen',
  DELIVERED_SEEN_NO_ROUTE: 'delivered_seen_no_route',
  CREATE_GROUP: 'create_group',
  ADD_GROUP_MEMBER: 'add_group_member',
  LEAVE_GROUP: 'leave_group',
  RENAME_GROUP: 'rename-group', //đổi tên nhóm
  DISSOLVE_GROUP: 'dissolve', //giải tán nhóm
  CHANGE_KEEP_PRIVATE_GROUP: 'keep_private', //giải tán nhóm
  BACKGROUND: 'background', // ẩn trình duyệt xuống khay hệ thống hoặc chuyển tab
  FOREGROUND: 'foreground', // mở trình duyệt, active mocha
  CLIENT_STATE: 'client_state', // lấy thông tin keyE2E
  ENABLE_E2E: 'enable_e2e', // cập nhật trạng thái chat bảo mật E2E
}

export const NODE_NAME = {
  IQ: 'iq',
  PRESENCE: 'presence',
  MESSAGE: 'message',
  STREAM: 'stream:stream',
}

export const NODE_TYPE_IQ = {
  RESULT: 'result',
  ERROR: 'error',
  GET: 'get',
  SET: 'set'
}

export const NODE_TYPE_PRESENSE = {
  AVAIABLE: 'avaiable',
  AVAILABLE: 'available',
  SUBSCRIBE: 'subscribe',
  UNAVAILABLE: 'unavailable',
}

export const NODE_TYPE_MESSAGE = {
  CHAT: 'chat',
  GROUPCHAT: 'groupchat',
  OFFICIAL: 'offical',
  EVENT: 'event',
}

export const NODE_SUBTYPE_PRESENCE = {
  CHANGE_DOMAIN: 'change_domain',
  BACKGROUND: 'background',
  FOREGROUND: 'foreground',
  CHANGE_OPERATOR: 'change_operator',
  DEACTIVE: 'deactive'
}

export const NODE_SUBTYPE_EVENT = {
  CONFIG: 'config',
  BLOCK_USER: 'block_user'
}

export const NODE_SUBTYPE_OFFICIAL = {
  DEEP_LINK: 'deeplink',
}

export const MESSAGE_EVENT_CONFIG_KEY = {
  GET_USER_INFO: 'GET_USER_INFO',
  BACKUP_MSG: 'BACKUP_MSG',
  GET_BLOCK_LIST: 'GET_BLOCK_LIST',
  RESTORE_MSG: 'RESTORE_MSG',
  GET_CONTACT: 'GET_CONTACT',
  UPLOAD_LOG: 'UPLOAD_LOG',
  ENABLE_DEBUG: 'ENABLE_DEBUG',
  DISABLE_DEBUG: 'DISABLE_DEBUG',
  UPLOG_DEBUG: 'UPLOG_DEBUG',
  CONFIG_LOG: 'CONFIG_LOG',
}

export const NODE_SUBTYPE_MESSAGE = {
  EVENT: 'event',
  TEXT: 'text',
  IMAGE: 'image',
  FILE: 'file_2',
  VIDEO: 'sharevideov2',
  DELETE: 'restore',
  CREATE: 'create',
  VOICE_STICKER: 'voicesticker',
  CONTACT: 'contact',
  LOCATION: 'location',
  AUDIO: 'voicemail',
  TYPING: 'typing',
  NOTIFY: 'notify',
  PIN_MSG: 'pin_msg',
  GROUP_AVATAR: 'groupAvatar',
  VOTE: 'vote',
  MAKE_ADMIN: 'makeAdmin',
  LEAVE: 'leave',
  GROUP_PRIVATE: 'groupPrivate',
  RECALL: 'recall',
  NOTIFICATION: 'notification',
  LIXI_NOTIFICATION: 'lixi_notification',
  JOIN_GROUP: 'join',
  CLIENT_STATE: 'client_state',
  ENABLE_E2E: 'enable_e2e',
}

export const NODE_ACTION_POLL = {
  CREATE_POLL: 'create_poll',
  SEND_POLL: 'send_poll',
  ADD_ITEM_POLL: 'add_item_poll'
}

export const MC = {
  AUTHENTYPE: 'token',
  COUNTRY: 'VN',
  CLIENT_TYPE: 'desktop',
  MECHANISM: 'token',
  NEW_SSID: '0',
  REVERSION: '15106',
  TO: 'reeng',
  TYPE: 'set',
  VERSION: '1.1'
}

export const NODE_TAG_NAME = {
  REACT: 'react'
}

export const REACT_MESSAGE_TYPE = {
  ADD: 'add',
  REMOVE: 'remove',
}

export const MESSAGE_EMOTIONS_ID = {
  LIKE: 0,
  HEART_RED: 1,
  SMILE: 2,
  STUNNED: 3,
  WANT_TO_CRY: 4,
  ANGRY: 5,
}

export const MESSAGE_EMOTIONS = [
  {
    icon: <IconLike />,
    id: MESSAGE_EMOTIONS_ID.LIKE,
  },
  {
    icon: <IconHeartRed />,
    id: MESSAGE_EMOTIONS_ID.HEART_RED,
  },
  {
    icon: <IconSmile />,
    id: MESSAGE_EMOTIONS_ID.SMILE,
  },
  {
    icon: <IconStunned />,
    id: MESSAGE_EMOTIONS_ID.STUNNED,
  },
  {
    icon: <IconWantToCry />,
    id: MESSAGE_EMOTIONS_ID.WANT_TO_CRY,
  },
  {
    icon: <IconAngry />,
    id: MESSAGE_EMOTIONS_ID.ANGRY,
  }
];

export const DIALOG_TYPE = {
  SETTING: 'setting', //mở dialog màn hình setting
  PERSONAL_INFORMATION: 'personal-information',//mở dialog màn hình thông tin tài khoản
  CONTACT: 'contacts', // mở dialog màn hình chia sẻ danh bạ
  MESSAGE_SEEN_BY: 'msg_seen_by', // mở dialog màn hình ai đã xem tin nhắn
  RENAME_GROUP: 'rename_group', // mở dialog màn hình thay đổi tên nhóm
  CREATE_GROUP: 'create_group', // mở dialog màn hình tạo nhóm
  SHOW_REACT: 'show_react', // mở dialog màn hình bày tỏ cảm xúc
  CREATE_POLL: 'create_poll', // mở dialog tạo khảo sát
  SHOW_POLL: 'show_poll', // mở dialog bình chọn khảo sát
  HIDE_TOPIC: 'hide_topic', // mở dialog nhập password (hoặc tạo password) khi ẩn/hiện cuộc trò chuyện
  SEND_EXCEL_TEXT: 'send_excel_text', // mở dialog gửi tin nhắn ảnh/text copy từ excel 
  FIND_MESSAGE_RESULT: 'find_message', // mở dialog kết quả tìm kiếm tin nhắn
  SHARE_INVITE_GROUP_LINK: 'share_invite_group_link', // mở dialog chia sẻ link mời vào nhóm
  JOIN_GROUP_LINK: 'join_group_link', // mở dialog hiển thị thông tin group từ link mời tham gia
  MOBILE_PASSWORD: 'mobile_password', // mở dialog hiển thị nhập mobile password
}

export const DIALOG_SETTING_TYPE = {
  HOME: 1, //màn hình setting chính
  CONVERSATION: 2, //màn hình setting Ẩn cuộc hội thoại
  LANGUAGE: 3, //màn hình setting Ngôn ngữ
  SAVED_MESSAGE: 4, // Màn hình Tin nhắn đã lưu
  HIDE_TOPIC: 5, // Màn hình ẩn cuộc hội thoại
}

export const LANGUAGE_SUPPORT = [
  {
    id: 1,
    name: "Tiếng Việt",
    image: vi,
    lang: 'vi'
  },
  {
    id: 2,
    name: "English",
    image: en,
    lang: 'en'
  },
  {
    id: 3,
    name: "ພາສາລາວ",
    image: lo,
    lang: 'lo'
  }
]

export const GROUP_ROLE = {
  MEMBER: 'member',
  ADMIN: 'owner'
}

export const USER_PROFILE = {
  GENDER: {
    UNKOWN: -1,
    FEMALE: 0,
    MALE: 1,
  },
  DEFAULT_BIRTHDAY: new Date('1990-01-01'),
  DEFAULT_AVATAR: '/img/broken_image.png',
}

export const TIME = {
  DAY_MILISECS: 24 * 60 * 60 * 1000,
  HOUR_MILISECS: 60 * 60 * 1000,
}

export const GROUP_MEMBER_VIEW_TYPE = {
  CREATE_GROUP: 'create_group',
  ADD_MEMBER: 'add_member',
  VIEW_MEMBER: 'view_member',
  UNKNOWN: 'unknown'
}


export const RESTORE_SUBTYPE_MESSAGE = {
  EVENT: 'event',
  TEXT: 'text',
  IMAGE: 'image',
  FILE: 'file',
  VIDEO: 'shareVideo',
  DELETE: 'restore',
  CREATE: 'create',
  VOICE_STICKER: 'voicesticker',
  CONTACT: 'contact',
  LOCATION: 'location',
  AUDIO: 'voicemail',
}

export const OPTIONS_SEARCH = {
  MESSAGE: 'message',
  CONTACT: 'contact',
  GROUP: 'group'
}

export const IQ_RESULT_CODE = {
  SUCCESS: 200,
  NOT_AUTHEN: 401,
  INVALID_MEMBER: 416,
  UNKNOWN_ERROR: 500,
  ERROR: 503,
}

export const ERROR_CODE = {
  SUCCESS: 200,
  INTERNAL_ERROR: 400,
  NOT_AUTHEN: 401,
  EXTERNAL_ERROR: 500,
}

export const NODE_NAME_XMPP = {
  BODY: 'body',
  IDREF: 'idref',
  PIN: 'pin',
  REACT: 'react',
  QUERY: 'query',
  MEMBER: 'member'
}

export const MESSAGE_ACTION_PIN = {
  PIN: 1,
  UNPIN: 0
}

export const BACKGROUND_CHATS = [bgr0, bgr1, bgr2, bgr3, bgr4, bgr5, bgr6, bgr7, bgr8, bgr9, bgr10, bgr11];

export const HIDE_TOPIC_ACTION = {
  SET_NEW_PASSWORD: 0,
  VERIFY_PASSWORD: 1,
  CHANGE_PASSWORD: 2,
}

export const HIDE_TOPIC_SUB_ACTION = {
  HIDE: 1,
  UNHIDE: 0,
  SELECT_TOPIC: 2,
}

export const GROUP_PRIVATE_STATE = {
  PRIVATE: '1',
  NON_PRIVATE: '0',
}

export const TAG_ALL = {
  MEMBER_ID: '@All',
  AVATAR: '/img/chat/tag-all.png'
}

export const DEEPLINK_SUPPORT = {
  SURVEY: 'mocha://survey'
}

export const CONTACT_LIST_SHOW_MODE = {
  PHONE_NUMBER: 'phone', // Hiển thị số điện thoại
}

export const DEFAULT_VOICE_STICKERS = {
  collectionId: 1,
  collectionName: "171719",
  numberSticker: 20,
  isDefault: true,
  collectionAvatar: null,
  collectionPreview: null,
  type: 0,
  tick: 0,
  order: 1,
  prefix: "/sticker/1",
  stickers: [
    {
      stickerId: 1,
      name: "1",
      type: "png",
      img: "1.png",
      voice: "1.mp3"
    },
    {
      stickerId: 2,
      name: "2",
      type: "png",
      img: "2.png",
      voice: "2.mp3"
    },
    {
      stickerId: 3,
      name: "3",
      type: "png",
      img: "3.png",
      voice: "3.mp3"
    },
    {
      stickerId: 4,
      name: "4",
      type: "png",
      img: "4.png",
      voice: "4.mp3"
    },
    {
      stickerId: 5,
      name: "5",
      type: "png",
      img: "5.png",
      voice: "5.mp3"
    },
    {
      stickerId: 6,
      name: "6",
      type: "png",
      img: "6.png",
      voice: "6.mp3"
    },
    {
      stickerId: 7,
      name: "7",
      type: "png",
      img: "7.png",
      voice: "7.mp3"
    },
    {
      stickerId: 8,
      name: "8",
      type: "png",
      img: "8.png",
      voice: "8.mp3"
    },
    {
      stickerId: 9,
      name: "9",
      type: "png",
      img: "9.png",
      voice: "9.mp3"
    },
    {
      stickerId: 10,
      name: "10",
      type: "png",
      img: "10.png",
      voice: "10.mp3"
    },
    {
      stickerId: 11,
      name: "11",
      type: "png",
      img: "11.png",
      voice: "11.mp3"
    },
    {
      stickerId: 12,
      name: "12",
      type: "png",
      img: "12.png",
      voice: "12.mp3"
    },
    {
      stickerId: 13,
      name: "13",
      type: "png",
      img: "13.png",
      voice: "13.mp3"
    },
    {
      stickerId: 14,
      name: "14",
      type: "png",
      img: "14.png",
      voice: "14.mp3"
    },
    {
      stickerId: 15,
      name: "15",
      type: "png",
      img: "15.png",
      voice: "15.mp3"
    },
    {
      stickerId: 16,
      name: "16",
      type: "png",
      img: "16.png",
      voice: "16.mp3"
    },
    {
      stickerId: 17,
      name: "17",
      type: "png",
      img: "17.png",
      voice: "17.mp3"
    },
    {
      stickerId: 18,
      name: "18",
      type: "png",
      img: "18.png",
      voice: "18.mp3"
    },
    {
      stickerId: 19,
      name: "19",
      type: "png",
      img: "19.png",
      voice: "19.mp3"
    },
    {
      stickerId: 20,
      name: "20",
      type: "png",
      img: "20.png",
      voice: "20.mp3"
    }
  ]
}

